import { memo, type ReactNode, type RefObject, useEffect, useRef } from 'react';

import type { ReplotChartNames } from '@revelio/replots';

import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { usePrimaryEntityKey } from '../../DataProvider/subsidiaries/use-primary-company';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import {
  isDynamicDivergingBarComboPlot,
  plotUsesShareOfSegmentPageSelections,
} from '../../PageConfigProvider/types/item';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { usePageState } from '../../PageState/PageStateProvider';
import { useSafeChart } from '../ChartProvider';
import {
  getFocusSelection,
  isDynamicEntityComparison,
  isSubfilterableDimension,
} from '../diverging-bar/dynamic-diverging-combo';
import { getExportTitle } from '../utils/getFileName';
import { ChartActionMenu, type ChartActionMenuProps } from './ChartActionMenu';
import { useChartMenuContext } from './ChartMenuContext';
import { getFormattedNames } from './data-download/converters/utils';
import { buildSelectionLabel } from './data-download/ppt-export/ppt-slide-generator';
import { usePageExport } from './PageExportProvider';

type ChartActionMenuWrapperProps = {
  chartId: string;
  title: string;
  primaryFilterId?: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  metric?: MetricValues;
  plotChartType?: ChartType;
  slim?: boolean;
  menu?: ChartActionMenuProps['menu'];
  printRef?: RefObject<HTMLDivElement>;
  expandModalContent?: ReactNode;
  data?: unknown;
  dataType?: string;
};

export const ChartActionMenuWrapper = memo(
  ({
    chartId,
    title,
    primaryFilterId,
    secondaryDimension,
    metric,
    plotChartType,
    slim,
    menu,
    printRef,
    expandModalContent,
    data: _data,
    dataType: _dataType,
  }: ChartActionMenuWrapperProps) => {
    const { downloadState, shareState } = useChartMenuContext();
    const { dataProviderSet, customData, customDataType } = downloadState;

    const data = _data || dataProviderSet || customData;
    const dataType =
      _dataType || dataProviderSet?.type || (customDataType ?? undefined);

    const { config: pageConfig } = usePageConfig();

    const { registerChart, unregisterChart, clearChartExportInfo } =
      usePageExport();

    const { item } = useSafeChart();
    const { pageState } = usePageState();
    const entityKey =
      usePrimaryEntityKey(item?.id === chartId ? item : undefined) ?? chartId;
    const { activePrimaryFilterLength, primaryFilter } =
      usePrimaryFilterState(primaryFilterId);

    const hasData = data && dataType;

    const shouldRender =
      hasData &&
      menu &&
      // biome-ignore lint/complexity/useIndexOf: migrated from develop
      menu.findIndex((item) => item === 'download-data') !== -1;

    const exportTitle = getExportTitle({
      pageName: pageConfig.name,
      primaryFilterId,
    });

    // Export titles name what the chart's dropdown narrows it to, read
    // through the same state and helpers the chart uses to fetch its data.
    const pickedEntity = pageState.primaryEntities?.[entityKey];
    const plottedEntity = isDynamicEntityComparison(item?.plot, primaryFilterId)
      ? getFocusSelection(primaryFilter?.primarySelections ?? [], pickedEntity)
      : pickedEntity;
    const hasSegmentDropdown =
      (item !== undefined && plotUsesShareOfSegmentPageSelections(item)) ||
      (isDynamicDivergingBarComboPlot(item?.plot) &&
        isSubfilterableDimension(
          item?.plot?.divergingBarCombo?.subfilterDimension
        ));
    const selectionLabel = buildSelectionLabel({
      entityName:
        activePrimaryFilterLength > 1 ? plottedEntity?.name : undefined,
      segmentNames: hasSegmentDropdown
        ? (pageState.selections?.[chartId] ?? []).map((segment) => segment.name)
        : [],
    });

    // Register / unregister chart for PPT export
    useEffect(() => {
      if (hasData) {
        registerChart(chartId, {
          title,
          exportTitle,
          chartType: shareState?.chartType as ReplotChartNames | undefined,
          plotChartType,
          selectedSegments: shareState?.chartProps?.selectedSegments as
            | { name: string }[]
            | undefined,
          data,
          dataType,
          captureRef: printRef,
          metric,
          selectionLabel,
          columnLabels: getFormattedNames(
            primaryFilterId,
            secondaryDimension,
            metric
          ),
        });
        if (selectionLabel === undefined) {
          clearChartExportInfo(chartId, ['selectionLabel']);
        }
      } else {
        unregisterChart(chartId);
      }
    }, [
      chartId,
      title,
      exportTitle,
      data,
      dataType,
      plotChartType,
      shareState,
      hasData,
      registerChart,
      unregisterChart,
      clearChartExportInfo,
      primaryFilterId,
      selectionLabel,
      secondaryDimension,
      metric,
      printRef,
    ]);

    const unregisterRef = useRef(unregisterChart);
    unregisterRef.current = unregisterChart;
    useEffect(() => {
      return () => {
        unregisterRef.current(chartId);
      };
    }, [chartId]);

    if (!shouldRender) {
      return null;
    }

    return (
      <ChartActionMenu
        menu={menu}
        title={title}
        selectionLabel={selectionLabel}
        dataType={dataType}
        data={data}
        shareState={shareState}
        primaryFilterId={primaryFilterId}
        secondaryDimension={secondaryDimension}
        metric={metric}
        plotChartType={plotChartType}
        pageName={pageConfig.name}
        slim={slim}
        printRef={printRef}
        expandModalContent={expandModalContent}
      />
    );
  }
);
